import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Drawer, Button, Space, Table, Typography, Modal, Form, Input, Row, Col, Select, message } from 'antd';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout } from '@utils/property';
import { getUserMessagesApi, checkMessagesApi } from '@services/MedProject';

const { Text } = Typography;

const reviewReasonStr = ['您的留言违反法律法规', '您的留言包含不文明用语/色情暴力/政治敏感', '您的留言涉及广告/垃圾信息', '您的留言泄露他人或自己的个人隐私信息', '您的留言违反平台/社区规则', '您的留言和医项目主题无关/缺乏实质性价值', '您的留言存在重复刷屏的可能性'];

let currentHiddenId; // 当前隐藏的留言 ID

export default function MessageReview({ messageReviewVisible, setMessageReviewVisible }) {
    const params = useParams();
    const projectId = (params?.projectId ?? params?.id) || 0; // 项目 ID

    const [form] = Form.useForm();
    const [hiddenForm] = Form.useForm();

    const [reasonModalOpen, setReasonModalOpen] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState({});

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const columns = [
        {
            title: '留言内容',
            dataIndex: 'content',
            key: 'content',
            width: 200,
        },
        {
            title: '留言时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
        },
        {
            title: '留言用户手机号',
            dataIndex: 'memMobile',
            key: 'memMobile',
            width: 200,
            render: (_, record) => <>{record.memMobile || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '留言用户昵称',
            dataIndex: 'memNickname',
            key: 'memNickname',
            width: 150,
            render: (_, record) => <>{record.memNickname || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '留言用户认证姓名',
            dataIndex: 'memRealname',
            key: 'memRealname',
            width: 200,
            render: (_, record) => <>{record.memRealname || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '是否匿名',
            dataIndex: 'userNameHidden',
            key: 'userNameHidden',
            width: 100,
            render: (_, record) => <>{record.userNameHidden === 1 ? '匿名' : '非匿名'}</>,
        },
        {
            title: '审核结果',
            dataIndex: 'reviewStatus',
            key: 'reviewStatus',
            width: 100,
            render: (_, record) => (
                <>
                    {record.reviewStatus === 0 && <Text>待审核</Text>}
                    {record.reviewStatus === 1 && <Text>已展示</Text>}
                    {record.reviewStatus === 2 && <Text>已隐藏</Text>}
                </>
            ),
        },
        {
            title: '隐藏原因',
            dataIndex: 'rejectionReason',
            key: 'rejectionReason',
            width: 200,
            render: (_, record) => <>{record.rejectionReason || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '审核人',
            dataIndex: 'reviewedBy',
            key: 'reviewedBy',
            width: 100,
            render: (_, record) => <>{record.reviewedBy || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '审核时间',
            dataIndex: 'reviewedAt',
            key: 'reviewedAt',
            width: 200,
            render: (_, record) => <>{record.reviewedAt || <Text type="secondary">&ndash;</Text>}</>,
        },
        {
            title: '操作',
            dataIndex: 'operations',
            key: 'operations',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="medium">
                    <Button type="link" onClick={() => onHidden(record.id)} disabled={record.reviewStatus === 2}>
                        留言隐藏
                    </Button>
                    <Button type="link" onClick={() => onShow(record.id)} disabled={record.reviewStatus === 1}>
                        留言展示
                    </Button>
                </Space>
            ),
        },
    ];

    const closeDrawer = () => {
        setMessageReviewVisible(false);
    };

    // 展示留言
    const onShow = async id => {
        const res = await checkMessagesApi({ id, reviewStatus: 1 });
        if (res.status_code === 200) {
            message.success('操作成功');
            getUserMessages();
        }
    };

    const onHidden = id => {
        setReasonModalOpen(true);
        currentHiddenId = id;
    };

    // 隐藏留言
    const handleHiddenOk = () => {
        hiddenForm.validateFields().then(async values => {
            const res = await checkMessagesApi({ id: currentHiddenId, reviewStatus: 2, rejectionReason: values.rejectionReason });
            if (res.status_code === 200) {
                message.success('操作成功');
                hiddenForm.resetFields();
                setReasonModalOpen(false);
                getUserMessages();
            }
        });
    };

    const handleHiddenCancel = () => {
        setReasonModalOpen(false);
        hiddenForm.resetFields();
    };

    const handleFilterFinish = values => {
        setFilter({ ...values });
    };

    const onReset = () => {
        form.resetFields();
        setFilter({});
    };

    const getUserMessages = () => {
        setLoading(true);
        getUserMessagesApi({ source: 'user', projectId, page, pageSize, ...filter }).then(res => {
            setLoading(false);
            if (res.status_code === 200 && res.data) {
                setData(res.data.list || []);
                setTotal(res.data.totalCount);
            }
        });
    };

    useEffect(() => {
        if (messageReviewVisible) {
            getUserMessages();
        }
    }, [filter, page, pageSize, messageReviewVisible]);

    return (
        <>
            <Drawer title="留言审核" className="site-module site-module-home" size="100%" placement="bottom" open={messageReviewVisible} onClose={closeDrawer} footer={null} closable={{ placement: 'end' }}>
                <div className="filter-form-box">
                    <Form {...filterFormItemLayout} onFinish={handleFilterFinish} form={form}>
                        <Row>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="留言内容" name="content">
                                    <Input allowClear placeholder="请输入内容" autoComplete="off" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="留言用户手机号" name="phone">
                                    <Input allowClear placeholder="请输入手机号" autoComplete="off" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="留言用户昵称" name="nickName">
                                    <Input allowClear placeholder="请输入昵称" autoComplete="off" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="审核结果" name="reviewStatus">
                                    <Select placeholder="请选择" allowClear>
                                        <Select.Option value={0}>待审核</Select.Option>
                                        <Select.Option value={1}>已展示</Select.Option>
                                        <Select.Option value={2}>已隐藏</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col className="filter-form-buttons" {...filterFormGridLayout}>
                                <Space>
                                    <Button type="default" onClick={onReset}>
                                        重置
                                    </Button>
                                    <Button type="primary" icon={<i className="bi bi-search"></i>} htmlType="submit">
                                        检索
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Table
                    columns={columns}
                    dataSource={data}
                    scroll={{ x: 1700 }}
                    loading={loading}
                    pagination={{
                        total: total,
                        pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                        showQuickJumper: true,
                        onChange: (page, pageSize) => {
                            setPage(page);
                            setPageSize(pageSize);
                        },
                        showTotal: () => `共 ${total} 条`,
                    }}
                />
            </Drawer>

            <Modal title="留言隐藏" open={reasonModalOpen} onOk={handleHiddenOk} onCancel={handleHiddenCancel}>
                <Form
                    name="basic"
                    form={hiddenForm}
                    labelCol={{
                        span: 24,
                    }}
                    wrapperCol={{
                        span: 24,
                    }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.Item label="请选择留言隐藏原因" name="rejectionReason" rules={[{ required: true, message: '请选择留言隐藏原因' }]}>
                        <Select placeholder="请选择" allowClear>
                            {reviewReasonStr.map((item, index) => (
                                <Select.Option value={item} key={index}>
                                    {item}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
